import { Component, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { APP_LOGO_URL, APP_NAME } from '@admin-core/constants/app.constants';
import { AuthService } from '@admin-core/services/auth.service';
import { TranslatePipe } from '@admin-core/i18n/translate.pipe';
import { LanguageSwitcherComponent } from '@admin-shared/components/language-switcher/language-switcher.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, TranslatePipe, LanguageSwitcherComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent {
  readonly appName = APP_NAME;
  readonly logoUrl = APP_LOGO_URL;
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly form;

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly auth: AuthService
  ) {
    this.form = this.fb.nonNullable.group({
      email: ['', Validators.required],
      password: ['', Validators.required]
    });

    if (this.auth.isLoggedIn()) {
      this.auth.refreshMe().subscribe({
        next: () => void this.router.navigateByUrl(this.returnUrl()),
        error: () => this.auth.logout()
      });
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    this.error.set(null);
    const { email, password } = this.form.getRawValue();
    this.auth.login(email, password).subscribe({
      next: () => {
        this.submitting.set(false);
        void this.router.navigateByUrl(this.returnUrl());
      },
      error: () => {
        this.submitting.set(false);
        this.error.set('login.error');
      }
    });
  }

  private returnUrl(): string {
    const url = this.route.snapshot.queryParamMap.get('returnUrl');
    return url?.startsWith('/admin') ? url : '/admin';
  }
}
